import { useEffect, useState } from "react";
import { NavLink, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  ListTodo,
  Wallet,
  Mic2,
  CalendarClock,
  Handshake,
  Sparkles,
  Megaphone,
  Inbox,
  Settings2,
  Users,
  LogOut,
  ArrowUpRight,
  Gauge,
  Search,
  Menu,
  X,
  FileText,
  Compass,
  BookOpen,
  Sun,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Video,
  Presentation,
} from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuth";
import { api } from "@/lib/api";
import { LangProvider, useLang } from "@/lib/adminLang";
import AnnouncementsAdmin from "@/pages/admin/AnnouncementsAdmin";
import SignupsAdmin from "@/pages/admin/SignupsAdmin";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";
import SakerAdmin from "@/pages/admin/saker/SakerAdmin";
import MoterAdmin from "@/pages/admin/moter/MoterAdmin";
import AssistentPanel from "@/pages/admin/assistent/AssistentPanel";
import DokumenterAdmin from "@/pages/admin/dokumenter/DokumenterAdmin";
import BudsjettAdmin from "@/pages/admin/budsjett/BudsjettAdmin";
import BrukereAdmin from "@/pages/admin/BrukereAdmin";
import PartnerDeckAdmin from "@/pages/admin/deck/PartnerDeckAdmin";
import ControlCenter from "@/pages/admin/eventos/ControlCenter";
import SpeakersOS from "@/pages/admin/eventos/SpeakersOS";
import ProgramOS from "@/pages/admin/eventos/ProgramOS";
import PartnersOS from "@/pages/admin/eventos/PartnersOS";
import ExperiencesOS from "@/pages/admin/eventos/ExperiencesOS";
import StrategyBoard from "@/pages/admin/strategi/StrategyBoard";
import PlaybookAdmin from "@/pages/admin/playbook/PlaybookAdmin";
import CommandPalette from "@/pages/admin/CommandPalette";
import "./admin/admin.css";

const NAV_GROUPS = [
  {
    label: "Event 2027",
    items: [
      { to: "/admin/overview", label: "Overview", testid: "admin-nav-overview", icon: Gauge },
      { to: "/admin/playbook", label: "Playbook", testid: "admin-nav-playbook", icon: BookOpen },
      { to: "/admin/dokumenter", label: "Dokumenter", testid: "admin-nav-dokumenter", icon: FileText },
      { to: "/admin/strategy", label: "Strategy", testid: "admin-nav-strategy", icon: Compass },
      { to: "/admin/speakers", label: "Speakers", testid: "admin-nav-speakers", icon: Mic2 },
      { to: "/admin/program", label: "Program", testid: "admin-nav-program", icon: CalendarClock },
      { to: "/admin/partners", label: "Partners", testid: "admin-nav-partners", icon: Handshake },
      { to: "/admin/deck", label: "Decks", testid: "admin-nav-deck", icon: Presentation },
      { to: "/admin/experience", label: "Experience", testid: "admin-nav-experience", icon: Sparkles },
      { to: "/admin/saker", label: "Saker", testid: "admin-nav-saker", icon: ListTodo },
      { to: "/admin/moter", label: "Meetings", testid: "admin-nav-moter", icon: Video },
      { to: "/admin/budsjett", label: "Budget", testid: "admin-nav-budsjett", icon: Wallet },
    ],
  },
  {
    label: "Website",
    items: [
      { to: "/admin/announcements", label: "Announcements", testid: "admin-nav-announcements", icon: Megaphone },
      { to: "/admin/signups", label: "Signups", testid: "admin-nav-signups", icon: Inbox },
      { to: "/admin/brukere", label: "Users", testid: "admin-nav-brukere", icon: Users },
      { to: "/admin/settings", label: "Settings", testid: "admin-nav-settings", icon: Settings2 },
    ],
  },
];

const BUDGET_SUB = [
  { to: "/admin/budsjett", label: "Overview", end: true, testid: "admin-nav-budget-overview" },
  { to: "/admin/budsjett/revenue", label: "Revenue", testid: "admin-nav-budget-revenue" },
  { to: "/admin/budsjett/costs", label: "Costs", testid: "admin-nav-budget-costs" },
  { to: "/admin/budsjett/cashflow", label: "Cash Flow", testid: "admin-nav-budget-cashflow" },
  { to: "/admin/budsjett/scenarios", label: "Scenarios", testid: "admin-nav-budget-scenarios" },
];

const BUDGET_CRUMB = { revenue: "Revenue", costs: "Costs", cashflow: "Cash Flow", scenarios: "Scenarios" };

const SEKSJON = {
  overview: ["Event 2027", "Overview"],
  playbook: ["Event 2027", "Playbook"],
  strategy: ["Event 2027", "Strategy"],
  speakers: ["Event 2027", "Speakers"],
  program: ["Event 2027", "Program"],
  partners: ["Event 2027", "Partners"],
  deck: ["Event 2027", "Decks"],
  experience: ["Event 2027", "Experience"],
  saker: ["Event 2027", "Saker"],
  moter: ["Event 2027", "Meetings"],
  budsjett: ["Event 2027", "Budget"],
  announcements: ["Website", "Announcements"],
  signups: ["Website", "Signups"],
  settings: ["Website", "Settings"],
};

const Boxed = ({ children }) => <div className="max-w-6xl">{children}</div>;

const SideInnhold = ({ admin, onLogout, signingOut, onNav, suffix = "", teller = { saker: 0, forfalt: 0, valg: 0 }, rail = false }) => {
  const loc = useLocation();
  const { t } = useLang();
  return (
  <>
    <div className={`border-b border-white/[0.07] ${rail ? "px-0 pt-6 pb-5 flex flex-col items-center gap-2.5" : "px-5 pt-6 pb-5"}`}>
      {rail ? (
        <>
          <p className="text-[12px] font-bold tracking-[0.03em] text-[#f5f1e8]">N<span className="text-[#F1E75A]">27</span></p>
          <span className="adm-live-dot" />
        </>
      ) : (
        <>
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#f5f1e8] leading-snug">
            Norwegian AI Summit <span className="text-[#F1E75A]">2027</span>
          </p>
          <p className="mt-2 flex items-center gap-1.5 text-[9px] uppercase tracking-[0.3em] text-[#565c66]">
            <span className="adm-live-dot" /> Event OS
          </p>
        </>
      )}
    </div>

    <nav className="flex-1 py-5 overflow-y-auto">
      {NAV_GROUPS.map((g) => (
        <div key={g.label} className="adm-side-gruppe mb-6">
          {!rail && <p className="adm-side-label px-4 pb-2">{t(g.label)}</p>}
          {g.items.map((n) => {
            const Ico = n.icon;
            return (
              <div key={n.to}>
                <NavLink
                  to={n.to}
                  onClick={onNav}
                  data-testid={`${n.testid}${suffix}`}
                  data-tip={t(n.label)}
                  className={({ isActive }) => `adm-side-link ${isActive ? "active" : ""}`}
                >
                  <Ico size={15} strokeWidth={1.75} className="adm-ico" />
                  {!rail && t(n.label)}
                  {n.to === "/admin/saker" && teller.saker > 0 && (rail ? (
                    <span className="adm-rail-dot" style={{ background: teller.forfalt > 0 ? "#EF5350" : "#8b9199" }} data-testid={`nav-teller-saker${suffix}`} />
                  ) : (
                    <span className="ml-auto pb-mono text-[9px] tracking-[0.08em] px-1.5 py-0.5 rounded-full" style={{ color: teller.forfalt > 0 ? "#EF5350" : "#8b9199", background: teller.forfalt > 0 ? "#EF53501c" : "rgba(139,145,153,0.14)" }} data-testid={`nav-teller-saker${suffix}`}>
                      {teller.saker}
                    </span>
                  ))}
                  {n.to === "/admin/strategy" && teller.valg > 0 && (rail ? (
                    <span className="adm-rail-dot" style={{ background: "#F59E0B" }} data-testid={`nav-teller-strategy${suffix}`} />
                  ) : (
                    <span className="ml-auto pb-mono text-[9px] tracking-[0.08em] px-1.5 py-0.5 rounded-full" style={{ color: "#F59E0B", background: "#F59E0B1c" }} data-testid={`nav-teller-strategy${suffix}`}>
                      {teller.valg}
                    </span>
                  ))}
                </NavLink>
                {!rail && n.to === "/admin/budsjett" && (suffix === "-m" || loc.pathname.startsWith("/admin/budsjett")) && (
                  <div className="pb-1" data-testid={`admin-nav-budget-sub${suffix}`}>
                    {BUDGET_SUB.map((s) => (
                      <NavLink
                        key={s.to}
                        to={s.to}
                        end={s.end}
                        onClick={onNav}
                        data-testid={`${s.testid}${suffix}`}
                        className={({ isActive }) => `adm-side-sublink ${isActive ? "active" : ""}`}
                      >
                        {t(s.label)}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </nav>

    <div className={`border-t border-white/[0.07] ${rail ? "px-0 py-4 flex flex-col items-center gap-2" : "px-4 py-4"}`}>
      {rail ? (
        <>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            data-tip={t("View site")}
            className="p-1.5 text-[#8b9199] hover:text-[#f5f1e8] transition-colors"
            data-testid={`admin-view-site${suffix}`}
          >
            <ArrowUpRight size={14} strokeWidth={1.75} />
          </a>
          <button
            onClick={onLogout}
            disabled={signingOut}
            data-tip={t("Sign out")}
            className="p-1.5 text-[#9aa1ab] hover:text-white transition-colors disabled:opacity-50"
            data-testid={`admin-logout${suffix}`}
          >
            <LogOut size={14} strokeWidth={1.75} />
          </button>
        </>
      ) : (
        <>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-[11px] text-[#8b9199] hover:text-[#f5f1e8] transition-colors mb-3"
            data-testid={`admin-view-site${suffix}`}
          >
            <ArrowUpRight size={12} /> {t("View site")}
          </a>
          <p className="text-[11px] text-[#6f7681] truncate" data-testid={`admin-current-email${suffix}`}>
            {admin?.email}
          </p>
          <button
            onClick={onLogout}
            disabled={signingOut}
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-[11.5px] font-medium text-[#9aa1ab] border border-white/[0.12] rounded-[2px] px-3 py-2 hover:text-white hover:border-white/[0.28] transition-colors disabled:opacity-50"
            data-testid={`admin-logout${suffix}`}
          >
            <LogOut size={12} />
            {signingOut ? t("Signing out…") : t("Sign out")}
          </button>
        </>
      )}
    </div>
  </>
  );
};

export default function AdminDashboard() {
  return (
    <LangProvider>
      <AdminShell />
    </LangProvider>
  );
}

function AdminShell() {
  const { admin, logout } = useAdminAuth();
  const { lang, setLang, t } = useLang();
  const nav = useNavigate();
  const loc = useLocation();
  const [signingOut, setSigningOut] = useState(false);
  const [cmdk, setCmdk] = useState(false);
  const [sideMobil, setSideMobil] = useState(false);
  const [lys, setLys] = useState(() => localStorage.getItem("nas_admin_theme") === "light");
  const [teller, setTeller] = useState({ saker: 0, forfalt: 0, valg: 0 });
  const [railKollaps, setRailKollaps] = useState(() => localStorage.getItem("nas_admin_rail") === "1");

  // Live nav-tellere: åpne saker (+forfalt) og åpne strategiske beslutninger
  useEffect(() => {
    let aktiv = true;
    const iDag = new Date().toISOString().slice(0, 10);
    Promise.allSettled([api.get("/admin/tasks"), api.get("/admin/os/strategy")]).then(([t, s]) => {
      if (!aktiv) return;
      const neste = { saker: 0, forfalt: 0, valg: 0 };
      if (t.status === "fulfilled") {
        const apne = (t.value.data.tasks || []).filter((x) => x.status !== "done");
        neste.saker = apne.length;
        neste.forfalt = apne.filter((x) => x.dueDate && x.dueDate < iDag).length;
      }
      if (s.status === "fulfilled") {
        neste.valg = (s.value.data.cards || []).filter((c) => c.status === "open" && !c.archived).length;
      }
      setTeller(neste);
    });
    return () => { aktiv = false; };
  }, [loc.pathname]);

  const byttTema = () => {
    setLys((v) => {
      localStorage.setItem("nas_admin_theme", v ? "dark" : "light");
      return !v;
    });
  };

  useEffect(() => {
    document.title = "Norwegian AI Summit · Admin";
    return () => { document.title = "Norwegian AI Summit"; };
  }, []);

  useEffect(() => { setSideMobil(false); }, [loc.pathname]);

  useEffect(() => {
    const key = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdk((v) => !v);
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, []);

  const onLogout = async () => {
    setSigningOut(true);
    await logout();
    nav("/admin/login", { replace: true });
  };

  const del = loc.pathname.split("/")[2] || "overview";
  let [gruppe, side] = SEKSJON[del] || ["Event 2027", ""];
  if (del === "budsjett") {
    gruppe = "Budget";
    side = BUDGET_CRUMB[loc.pathname.split("/")[3]] || "Overview";
  }
  const tema = lys ? "adm-light" : "adm-dark";

  return (
    <div data-testid="admin-dashboard" className={`min-h-screen adm-canvas ${tema} text-neutral-900 flex`}>
      {/* Sidebar — desktop */}
      <aside className={`adm-side ${railKollaps ? "w-[64px] adm-rail-kollaps" : "w-[248px]"} shrink-0 min-h-screen hidden lg:flex flex-col sticky top-0 max-h-screen z-40 transition-all duration-200`}>
        <SideInnhold admin={admin} onLogout={onLogout} signingOut={signingOut} teller={teller} rail={railKollaps} />
        <button
          type="button"
          onClick={() => { const v = !railKollaps; setRailKollaps(v); localStorage.setItem("nas_admin_rail", v ? "1" : "0"); }}
          data-tip={railKollaps ? "Utvid menyen" : undefined}
          className={`flex items-center justify-center gap-2 pb-mono text-[9px] tracking-[0.2em] hover:text-[#f5f1e8] hover:bg-white/[0.05] py-3 transition-colors ${railKollaps ? "order-first border-b text-[#c7ccd3]" : "border-t text-[#9aa1ab]"} border-white/[0.07]`}
          data-testid="rail-toggle"
        >
          {railKollaps ? <PanelLeftOpen size={15} strokeWidth={1.75} /> : (<><PanelLeftClose size={14} strokeWidth={1.5} /> SKJUL</>)}
        </button>
      </aside>

      {/* Sidebar — mobil overlay */}
      {sideMobil && (
        <div className="fixed inset-0 z-50 flex lg:hidden" data-testid="admin-mobil-side">
          <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={() => setSideMobil(false)} />
          <aside className="adm-side adm-side-inn relative w-[260px] h-full flex flex-col">
            <button
              type="button"
              onClick={() => setSideMobil(false)}
              className="absolute top-4 right-3 p-1.5 text-[#8b9199] hover:text-white transition-colors"
              data-testid="admin-mobil-side-lukk"
            >
              <X size={16} />
            </button>
            <SideInnhold admin={admin} onLogout={onLogout} signingOut={signingOut} onNav={() => setSideMobil(false)} suffix="-m" teller={teller} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className={`flex-1 flex flex-col min-w-0 adm-canvas ${tema}`}>
        <div className="adm-topbar sticky top-0 z-30">
          <div className="mx-auto w-full max-w-[1560px] h-full flex items-center gap-3 px-4 sm:px-6 lg:px-10">
            <button
              type="button"
              onClick={() => setSideMobil(true)}
              className="lg:hidden -ml-1 p-1.5 adm-cmdk-ikon hover:opacity-70 transition-opacity"
              data-testid="admin-meny-btn"
            >
              <Menu size={17} strokeWidth={1.75} />
            </button>
            <p className="adm-crumb truncate">
              {t(gruppe)} <span className="skille">/</span> <b>{t(side)}</b>
            </p>
            <button
              type="button"
              onClick={byttTema}
              className="adm-sok ml-auto"
              title={lys ? t("Switch to dark theme") : t("Switch to light theme")}
              data-testid="admin-tema-btn"
            >
              {lys ? <Moon size={12.5} strokeWidth={1.75} /> : <Sun size={12.5} strokeWidth={1.75} />}
            </button>
            <button
              type="button"
              onClick={() => setLang(lang === "no" ? "en" : "no")}
              className="adm-sok"
              title={t("Switch language")}
              data-testid="admin-lang-btn"
            >
              <span className="text-[10px] font-semibold tracking-[0.1em]">{lang === "no" ? "EN" : "NO"}</span>
            </button>
            <button
              type="button"
              onClick={() => setCmdk(true)}
              className="adm-sok"
              data-testid="admin-sok-btn"
            >
              <Search size={12.5} strokeWidth={1.75} />
              <span className="hidden sm:inline">{t("Search")}</span>
              <kbd className="adm-kbd hidden sm:inline-block">⌘K</kbd>
            </button>
          </div>
        </div>

        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-10 py-6 lg:py-8">
          <div className="mx-auto w-full max-w-[1560px]">
            <div key={loc.pathname} className="adm-page">
              <Routes>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<ControlCenter />} />
                <Route path="playbook" element={<PlaybookAdmin />} />
                <Route path="strategy" element={<StrategyBoard />} />
                <Route path="speakers" element={<SpeakersOS />} />
                <Route path="dokumenter" element={<DokumenterAdmin />} />
                <Route path="program" element={<ProgramOS />} />
                <Route path="saker" element={<SakerAdmin />} />
                <Route path="moter" element={<Boxed><MoterAdmin /></Boxed>} />
                <Route path="budsjett" element={<BudsjettAdmin />} />
                <Route path="budsjett/:sub" element={<BudsjettAdmin />} />
                <Route path="partners" element={<PartnersOS />} />
                <Route path="deck" element={<PartnerDeckAdmin />} />
                <Route path="lawdeck" element={<Navigate to="/admin/deck" replace />} />
                <Route path="experience" element={<ExperiencesOS />} />
                <Route path="announcements" element={<Boxed><AnnouncementsAdmin /></Boxed>} />
                <Route path="signups" element={<Boxed><SignupsAdmin /></Boxed>} />
                <Route path="brukere" element={<Boxed><BrukereAdmin /></Boxed>} />
                <Route path="settings" element={<Boxed><SettingsAdmin /></Boxed>} />
              </Routes>
            </div>
          </div>
        </main>
      </div>

      <AssistentPanel />
      <CommandPalette open={cmdk} onClose={() => setCmdk(false)} />
    </div>
  );
}
